// src/contexts/OpenIntentionPeriodContext.tsx
//
// Single source of truth for "is a teaching-intentions submission period open
// right now". Fetched once and shared so the TopBar, Sidebar, and staff
// dashboard all read the same state without duplicate queries.

import React, { createContext, useContext, useEffect, useState } from 'react';
import { intentionPeriodsService } from '../services/intentionPeriodsService';
import { useAuth } from './AuthContext';
import type { IntentionPeriod } from '../types/intentionPeriods';

interface OpenIntentionPeriodValue {
  openPeriod: IntentionPeriod | null;
  isOpen: boolean;
  loading: boolean;
}

const OpenIntentionPeriodContext = createContext<OpenIntentionPeriodValue>({
  openPeriod: null,
  isOpen: false,
  loading: true,
});

export const OpenIntentionPeriodProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { user } = useAuth();
  const [openPeriod, setOpenPeriod] = useState<IntentionPeriod | null>(null);
  const [loading, setLoading] = useState(true);

  // Re-fetch whenever the active user changes — most importantly when an admin
  // starts or stops impersonating a teacher. Impersonation swaps the user via
  // SPA navigation (no full reload), so a once-only fetch would otherwise stay
  // stale and hide the "Submit My Intentions" entry points for the impersonated
  // teacher even when a period is open.
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const period = await intentionPeriodsService.getCurrentOpenPeriod();
        if (!cancelled) setOpenPeriod(period);
      } catch (error) {
        console.error('OpenIntentionPeriodProvider: failed to load open period', error);
        if (!cancelled) setOpenPeriod(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user?.email]);

  return (
    <OpenIntentionPeriodContext.Provider
      value={{ openPeriod, isOpen: openPeriod !== null, loading }}
    >
      {children}
    </OpenIntentionPeriodContext.Provider>
  );
};

export function useOpenIntentionPeriod(): OpenIntentionPeriodValue {
  return useContext(OpenIntentionPeriodContext);
}
